import React, { useMemo } from "react";
import { Card, Typography } from "antd";
import { useNavigate, generatePath } from "react-router-dom";
import { ROUTES } from "../../../config/routes";
import { PhotoIcon } from "@heroicons/react/24/outline";
import { ViewfinderCircleIcon } from "@heroicons/react/24/outline";
import { BookmarkIcon } from "@heroicons/react/24/outline";
import { BookmarkIcon as BookmarkIconSolid } from "@heroicons/react/24/solid";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { ReactComponent as MosquitoIcon } from "../../../assets/icons/MosquitoVector.svg";

import { CatalogItem, listCatalogRequest } from "../../../types";
import { formatDate } from "../../../utils/helpers";
import { generateDisplayId } from "../../../utils/displayId";
import { toggleSelection } from "../../../reducers/catalogSelectionSlice";
import {
  useAppDispatch,
  useAppSelector,
  useBookmarkToggle,
} from "../../../hooks";
import "./index.css";

const { Text } = Typography;

type ImageCardProps = CatalogItem & {
  onClick?: () => void;
  catalogId?: string;
  queryParams?: listCatalogRequest;
};

const ImageCard: React.FC<ImageCardProps> = (props) => {
  const {
    imageType,
    img,
    device_name,
    trap_site_name,
    onClick,
    catalogId = "",
    is_bookmarked = false,
    queryParams,
    created_at,
    source,
    id,
  } = props;

  const navigate = useNavigate();

  const capture_uuid = source === "detection" ? props.capture_uuid : props.uuid;
  const specimen_uuid = source === "detection" ? props.specimen_id : undefined;
  const contributedSpeciesName =
    source === "detection" ? props.contributed_species : "";
  const isContributed = contributedSpeciesName?.length > 0;

  // For detections, we might want to show algorithm info if available.
  // However, CatalogDetection doesn't have algorithmId in its type.
  // We'll leave it as empty for now to avoid compilation errors.
  const algorithmDetectionType = source === "detection" ? props.imageType : "";
  const algorithmSpeciesName = "";

  const dispatch = useAppDispatch();
  const isSelected = useAppSelector((state) =>
    state.catalogSelection.selectedIds.includes(catalogId),
  );

  const handleToggleSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(toggleSelection(catalogId));
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick();
      return;
    }

    if (imageType === "Mesh") {
      navigate(generatePath(ROUTES.mesh_detail, { id }));
    } else {
      navigate(generatePath(ROUTES.specimen_detail, { id }));
    }
  };

  const { isBookmarking, handleToggleBookmark } = useBookmarkToggle({
    catalogItem: props,
    queryParams,
  });

  const displayTitle = useMemo(
    () =>
      generateDisplayId({
        captureUuid: capture_uuid,
        specimenUuid: imageType === "Mesh" ? undefined : specimen_uuid,
      }),
    [capture_uuid, imageType, specimen_uuid],
  );

  const displaySubtitle = useMemo(() => {
    return `${trap_site_name?.length > 0 ? trap_site_name : "Unassigned Location"} - ${device_name?.length > 0 ? device_name : ""}`;
  }, [trap_site_name, device_name]);

  return (
    <Card
      hoverable
      className="card-image"
      onClick={handleCardClick}
      styles={{ body: { padding: "12px" } }}
    >
      {/* Header: Icon + Title */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          {imageType === "Mesh" ? (
            <ViewfinderCircleIcon className="w-5 h-5 text-[#005F8F]" />
          ) : (
            <PhotoIcon className="w-5 h-5 text-[#005F8F]" />
          )}
          <h3 className="text-[#005F8F] font-bold text-base m-0 leading-tight">
            {displayTitle}
          </h3>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (!isBookmarking) handleToggleBookmark();
          }}
          className={`focus:outline-none cursor-pointer transition-opacity ${isBookmarking ? "opacity-50 cursor-wait" : "hover:opacity-80"}`}
          disabled={isBookmarking}
        >
          {is_bookmarked ? (
            <BookmarkIconSolid className="w-5 h-5 text-[#005F8F]" />
          ) : (
            <BookmarkIcon className="w-5 h-5 text-[#005F8F]" />
          )}
        </button>
      </div>
      {/* Subtitle + Date */}
      <div className="flex justify-between items-center text-[#003952] text-xs mb-2 gap-2 min-w-0">
        <span className="truncate">{displaySubtitle}</span>
        <span className="shrink-0">{formatDate(created_at)}</span>
      </div>
      {/* Status */}
      {imageType !== "Mesh" ? (
        isContributed ? (
          <div className="flex items-center gap-2 mb-1">
            <MosquitoIcon className="w-5 h-5 fill-current" />
            <span className="font-medium text-sm">
              {contributedSpeciesName}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-1 text-[#FF6B6B]">
            <MosquitoIcon className="w-5 h-5 fill-current" />
            <span className="font-medium text-sm">
              {algorithmDetectionType === "Mosquito"
                ? algorithmSpeciesName || "Undefined"
                : algorithmDetectionType || "Unknown"}
            </span>
          </div>
        )
      ) : (
        <div className="h-5 mb-1" />
      )}
      {/* Image */}
      <div className="flex justify-center rounded-sm overflow-hidden aspect-15/13">
        <img
          key={img}
          src={img}
          alt={`${imageType}-${specimen_uuid || capture_uuid}-${created_at}`}
          className="w-full h-full object-cover"
          crossOrigin="anonymous"
        />
      </div>
      <div
        className="mt-2 flex items-center gap-2 cursor-pointer overflow-hidden"
        onClick={handleToggleSelection}
      >
        {isSelected ? (
          <CheckCircleIcon className="w-5 h-5 text-[#0EA5E9]" />
        ) : (
          <div className="w-5 h-5 flex items-center justify-center">
            <div className="w-4 h-4 border border-[#005F8F] rounded-lg" />
          </div>
        )}
        <Text className="truncate">Select for download</Text>
      </div>
    </Card>
  );
};

export default ImageCard;
